import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useWordPress } from '../context/WordPressContext';
import { supabase } from '../config/supabase';

/**
 * WordPress Authentication Bridge
 * 
 * Automatically authenticates users when they have a valid WordPress auth token.
 * This solves the "must login again" problem when navigating between WordPress pages.
 * 
 * How it works:
 * 1. Detects wp_auth_token URL parameter
 * 2. Validates token with WordPress REST API
 * 3. Gets user email from WordPress
 * 4. Signs into Supabase using magic link or password
 * 5. Cleans up URL parameter
 * 6. AUTOMATICALLY links user to store if store_id is provided
 */
export function WordPressAuthBridge({ children }) {
  const { user, loading: authLoading } = useAuth();
  const {
    isInWordPress,
    wordpressUrl,
    wpRestSession,
    wpRestSessionExpiry,
    setWpRestSession,
    clearWpRestSession,
    setWordPressContext,
  } = useWordPress();
  const [bridgeStatus, setBridgeStatus] = useState('idle'); // idle, checking, success, failed
  const [error, setError] = useState(null);
  const expiryTimerRef = useRef(null);

  const dispatchSessionEvent = useCallback((eventName, detail) => {
    try {
      window.dispatchEvent(new CustomEvent(eventName, { detail }));
    } catch (eventError) {
      console.warn(`[WP Auth Bridge] Failed to dispatch ${eventName}`, eventError);
    }
  }, []);

  const hasValidRestSession = useCallback(() => {
    if (wpRestSession) {
      if (!wpRestSessionExpiry) {
        return true;
      }
      return wpRestSessionExpiry > Date.now();
    }

    try {
      const token = sessionStorage.getItem('vfr-wp-rest-session');
      if (!token) {
        return false;
      }
      const expiryRaw = sessionStorage.getItem('vfr-wp-rest-session-expiry');
      if (!expiryRaw) {
        return true;
      }
      const expiryValue = parseInt(expiryRaw, 10);
      if (Number.isNaN(expiryValue)) {
        return true;
      }
      return expiryValue > Date.now();
    } catch (_) {
      return false;
    }
  }, [wpRestSession, wpRestSessionExpiry]);

  const handleSessionReady = useCallback((token, ttlSeconds, wpOrigin) => {
    if (!token) {
      return;
    }
    setWpRestSession(token, ttlSeconds);
    if (wpOrigin && typeof setWordPressContext === 'function') {
      setWordPressContext({ url: wpOrigin });
    }

    const expiresAt = typeof ttlSeconds === 'number' && ttlSeconds > 0
      ? Date.now() + ttlSeconds * 1000
      : null;

    dispatchSessionEvent('vfr-wp-session-ready', { token, ttlSeconds, expiresAt });
  }, [setWpRestSession, setWordPressContext, dispatchSessionEvent]);

  const mintSessionViaCookie = useCallback(async (wpOrigin) => {
    if (!wpOrigin) {
      return false;
    }
    try {
      const sessionRes = await fetch(`${wpOrigin}/wp-json/virtual-fitting-room/v1/products/session`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!sessionRes.ok) {
        return false;
      }

      const sessionData = await sessionRes.json();
      if (sessionData?.rest_session) {
        handleSessionReady(sessionData.rest_session, sessionData.ttl_seconds, wpOrigin);
        console.log('[WP Auth Bridge] ✅ Minted WP REST session via cookie');
        return true;
      }
    } catch (cookieError) {
      console.warn('[WP Auth Bridge] Fallback session mint failed', cookieError);
    }
    return false;
  }, [handleSessionReady]);

  useEffect(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }

    if (!isInWordPress || !wpRestSession || !wpRestSessionExpiry) {
      return;
    }

    const msUntilExpiry = wpRestSessionExpiry - Date.now();
    if (msUntilExpiry <= 0) {
      clearWpRestSession();
      dispatchSessionEvent('vfr-wp-session-expired', { reason: 'ttl_expired' });
      return;
    }

    expiryTimerRef.current = setTimeout(() => {
      clearWpRestSession();
      dispatchSessionEvent('vfr-wp-session-expired', { reason: 'ttl_expired' });
    }, msUntilExpiry);

    return () => {
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = null;
      }
    };
  }, [isInWordPress, wpRestSession, wpRestSessionExpiry, clearWpRestSession, dispatchSessionEvent]);

  const guessWordPressUrl = useCallback(() => {
    try {
      // Try to get parent URL (won't work cross-origin but worth trying)
      if (window.parent && window.parent !== window) {
        return window.parent.location.origin;
      }
    } catch (e) {
      // Cross-origin restriction
    }

    // Fallback: use document referrer
    if (document.referrer) {
      const url = new URL(document.referrer);
      return url.origin;
    }

    return null;
  }, []);

  const validateAndAuthenticate = useCallback(async (token, precomputedWpUrl = null) => {
    try {
      // Step 1: Validate token with WordPress
      console.log('[WP Auth Bridge] Validating token with WordPress...');

      const wpUrl = precomputedWpUrl || wordpressUrl || window.location.ancestorOrigins?.[0] || guessWordPressUrl();
      if (!wpUrl) {
        throw new Error('Unable to determine WordPress origin for iframe auth');
      }
      const validationUrl = `${wpUrl}/wp-json/vfr/v1/iframe-auth?token=${encodeURIComponent(token)}`;

      console.log('[WP Auth Bridge] Validation URL:', validationUrl);

      const response = await fetch(validationUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important for WordPress cookies
      });

      const data = await response.json();
      console.log('[WP Auth Bridge] WordPress validation response:', data);

      if (!data.valid) {
        throw new Error(data.message || 'Token validation failed');
      }

      // Persist short-lived WP REST session token for subsequent REST calls from Products tab
      try {
        if (data.rest_session) {
          handleSessionReady(data.rest_session, data.ttl_seconds, data.wordpress_url || wpUrl);
        } else if (data.wordpress_url && typeof setWordPressContext === 'function') {
          setWordPressContext({ url: data.wordpress_url });
        }
        setError(null);
      } catch (e) {
        console.warn('[WP Auth Bridge] Failed to persist WP rest session token', e);
      }

      // Store store_id for linking
      if (data.store_id) {
        console.log('[WP Auth Bridge] Store ID received:', data.store_id);
        sessionStorage.setItem('vfr-pending-store-id', data.store_id);
      }

      // Step 2: Authenticate with Supabase
      const userEmail = data.user_email;
      console.log('[WP Auth Bridge] Authenticating Supabase user:', userEmail);

      // First, try to restore existing session from localStorage backup
      const sessionBackup = localStorage.getItem('vfr-session-backup');
      if (sessionBackup) {
        try {
          const sessionData = JSON.parse(sessionBackup);
          if (sessionData.user?.email === userEmail) {
            console.log('[WP Auth Bridge] Found matching session backup, attempting restore...');

            const { data: sessionResult, error: restoreError } = await supabase.auth.setSession({
              access_token: sessionData.access_token,
              refresh_token: sessionData.refresh_token,
            });

            if (!restoreError && sessionResult?.session) {
              console.log('[WP Auth Bridge] ✅ Session restored successfully!');
              setBridgeStatus('success');
              return;
            } else {
              console.log('[WP Auth Bridge] Session restore failed or expired:', restoreError?.message);
            }
          } else {
            console.log('[WP Auth Bridge] Session backup email mismatch');
          }
        } catch (e) {
          console.warn('[WP Auth Bridge] Error parsing session backup:', e);
        }
      }

      // Session restore failed - try to mint WP REST session via cookie if user is logged in to WP
      try {
        const minted = await mintSessionViaCookie(wpUrl);
        if (minted) {
          setError(null);
        }
      } catch (e2) {
        console.warn('[WP Auth Bridge] Fallback session mint failed', e2);
      }

      // Mark as failed for Supabase auth; UI can still load read-only and Products tab can use REST session if minted
      setBridgeStatus('failed');
      setError(null);

    } catch (err) {
      console.error('[WP Auth Bridge] Error:', err);
      setBridgeStatus('failed');
      setError(err.message);
    }
  }, [wordpressUrl, handleSessionReady, setWordPressContext, guessWordPressUrl, mintSessionViaCookie]);

  useEffect(() => {
    if (!isInWordPress) {
      return;
    }

    const handleMessage = (event) => {
      if (!event?.data || typeof event.data !== 'object') {
        return;
      }

      if (event.data.type === 'wordpress-auth-token') {
        const {
          token,
          restSession,
          ttlSeconds,
          wordpressUrl: wpOrigin,
        } = event.data;

        if (wpOrigin && typeof setWordPressContext === 'function') {
          setWordPressContext({ url: wpOrigin });
        }

        if (restSession) {
          handleSessionReady(restSession, ttlSeconds || null, wpOrigin || null);
          setBridgeStatus('success');
          setError(null);
          return;
        }

        if (!token) {
          return;
        }

        setBridgeStatus('checking');
        validateAndAuthenticate(token, wpOrigin || null)
          .catch((err) => {
            console.error('[WP Auth Bridge] Token refresh failed', err);
            setBridgeStatus('failed');
            setError(err.message);
          });
      }

      if (event.data.type === 'wordpress-auth-token-error') {
        console.warn('[WP Auth Bridge] Parent failed to refresh auth token', event.data?.error);
        setBridgeStatus('checking');
        setError(event.data?.error || 'Refreshing WordPress session…');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [isInWordPress, validateAndAuthenticate, setWordPressContext, handleSessionReady]);

  useEffect(() => {
    if (!isInWordPress) {
      return;
    }

    const currentUrl = new URL(window.location.href);
    const hasRefreshParam = currentUrl.searchParams.get('refresh_wp_auth') === '1';

    if (authLoading) {
      return;
    }

    const restSessionIsValid = hasValidRestSession();

    if (!hasRefreshParam && user && restSessionIsValid) {
      console.log('[WP Auth Bridge] User authenticated and WP rest session present, skipping bridge');
      return;
    }

    const runBridge = async () => {
      const wpOrigin = wordpressUrl || window.location.ancestorOrigins?.[0] || guessWordPressUrl();

      if (hasRefreshParam) {
        console.log('[WP Auth Bridge] Refresh auth requested, attempting to mint WP REST session via cookie');
        setBridgeStatus('checking');
        const success = await mintSessionViaCookie(wpOrigin);
        setBridgeStatus(success ? 'success' : 'failed');
        if (!success) {
          setError('Failed to refresh WordPress REST session');
        } else {
          setError(null);
        }
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const authToken = urlParams.get('wp_auth_token');

      if (!authToken) {
        console.log('[WP Auth Bridge] No URL token, requesting auth from parent via postMessage');
        // Ask parent frame to send a fresh token via postMessage
        try {
          window.parent.postMessage({ type: 'vfr-request-auth-token' }, '*');
        } catch (e) {
          console.warn('[WP Auth Bridge] Failed to request token from parent', e);
        }
        return;
      }

      console.log('[WP Auth Bridge] Auth token detected, starting validation...');
      setBridgeStatus('checking');
      await validateAndAuthenticate(authToken, wpOrigin);
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('wp_auth_token');
      window.history.replaceState({}, '', newUrl);
    };

    runBridge().catch((err) => {
      console.error('[WP Auth Bridge] Bridge run failed', err);
      setBridgeStatus('failed');
      setError(err.message);
    });
  }, [
    isInWordPress,
    user,
    authLoading,
    wordpressUrl,
    wpRestSession,
    wpRestSessionExpiry,
    clearWpRestSession,
    hasValidRestSession,
    mintSessionViaCookie,
    validateAndAuthenticate,
    guessWordPressUrl,
  ]);

  // Show loading state while checking token
  if (bridgeStatus === 'checking') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-surface-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-surface-200 border-t-brand-600 mx-auto mb-4"></div>
          <p className="text-surface-600">Connecting to WordPress...</p>
          <p className="text-sm text-surface-500 mt-2">Authenticating your session</p>
        </div>
      </div>
    );
  }

  // Show error if bridge failed (but still render children so user can sign in manually)
  if (bridgeStatus === 'failed' && error) {
    console.log('[WP Auth Bridge] Bridge failed, rendering children for manual login');
  }

  // Render children normally
  return children;
}

